import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Text, Icon, Badge } from '@components/base/display';
import { HStack } from '@components/base/layout';
import { Tabs } from '@components/base/navigation';
import {
  categories,
  sections,
  getShowcasesByCategory,
  type ComponentCategory,
  type ComponentSection,
} from '../showcases';

interface ComponentExplorerSidebarProps {
  currentId?: string;
}

export function ComponentExplorerSidebar({
  currentId,
}: Readonly<ComponentExplorerSidebarProps>) {
  const [expandedCategories, setExpandedCategories] = useState<
    Set<ComponentCategory>
  >(new Set(categories.map((c) => c.id)));

  // Determine active section from currently selected component
  const activeSection = (() => {
    if (!currentId) return sections[0]?.id ?? 'base';
    const showcase = categories.find((cat) => {
      const showcases = getShowcasesByCategory(cat.id);
      return showcases.some((s) => s.id === currentId);
    });
    return showcase?.section ?? sections[0]?.id ?? 'base';
  })();

  const toggleCategory = (categoryId: ComponentCategory) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  return (
    <nav className="flex flex-col h-full">
      {/* Section tabs */}
      <div className="px-3 pt-3">
        <Tabs defaultValue={activeSection} variant="contained">
          <Tabs.List>
            {sections.map((section) => (
              <Tabs.Tab key={section.id} value={section.id}>
                {section.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {sections.map((section) => {
            const sectionCategories = categories.filter(
              (c) => c.section === section.id,
            );

            return (
              <Tabs.Panel key={section.id} value={section.id}>
                <SectionContent
                  sectionCategories={sectionCategories}
                  expandedCategories={expandedCategories}
                  toggleCategory={toggleCategory}
                  currentId={currentId}
                />
              </Tabs.Panel>
            );
          })}
        </Tabs>
      </div>
    </nav>
  );
}

function SectionContent({
  sectionCategories,
  expandedCategories,
  toggleCategory,
  currentId,
}: Readonly<{
  sectionCategories: { id: ComponentCategory; label: string; section: ComponentSection }[];
  expandedCategories: Set<ComponentCategory>;
  toggleCategory: (id: ComponentCategory) => void;
  currentId?: string;
}>) {
  if (sectionCategories.length === 0) {
    return (
      <div className="px-1 py-4">
        <Text as="p" size="sm" color="muted" className="italic">
          Coming soon
        </Text>
      </div>
    );
  }

  return (
    <div className="-mx-3">
      {sectionCategories.map((category) => {
        const showcases = getShowcasesByCategory(category.id);
        const isExpanded = expandedCategories.has(category.id);

        return (
          <div key={category.id} className="mb-0.5">
            {/* Category toggle */}
            <button
              type="button"
              onClick={() => toggleCategory(category.id)}
              className="flex w-full items-center justify-between px-4 py-1.5 text-left transition-colors hover:bg-secondary-100"
            >
              <HStack gap={2} align="center">
                <Icon
                  icon={isExpanded ? ChevronDown : ChevronRight}
                  size="xs"
                  color="muted"
                />
                <Text as="span" size="sm" weight="medium">
                  {category.label}
                </Text>
              </HStack>
              <Badge size="sm" pill>
                {showcases.length}
              </Badge>
            </button>

            {/* Component items */}
            {isExpanded && (
              <div className="pb-1">
                {showcases.map((showcase) => {
                  const isActive = currentId === showcase.id;
                  return (
                    <Link
                      key={showcase.id}
                      to={`/component-explorer/${showcase.id}`}
                      className={[
                        'flex items-center py-1 text-[13px] transition-colors',
                        isActive
                          ? 'bg-primary-50 text-primary-700 font-medium border-l-[3px] border-l-primary-600 pl-[29px] pr-4'
                          : 'text-secondary-500 hover:bg-secondary-100 hover:text-secondary-700 pl-8 pr-4',
                      ].join(' ')}
                    >
                      {showcase.name}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
